<template>
  <div class="pb-6 pt-2">
    <label class="sr-only" for="voucher">{{ t['w.ostukorv.soodustus.promo_code']() }}</label>

    <div class="relative flex items-center rounded-button border text-lg md:text-base">
      <Icon name="coupon" class="absolute ml-4 mr-3 size-4" />
      <input
        id="voucher"
        v-model="voucher"
        class="w-full bg-transparent py-3 pl-11 pr-2 outline-none placeholder:text-text focus:placeholder:text-text-medium"
        type="text"
        :placeholder="focused ? t['w.basket.summary.voucher_placeholder']() : t['w.basket.summary.voucher']()"
        @focus="focused = true"
        @blur="focused = false"
        @keydown.enter.prevent="apply"
      />
      <button v-show="isDirty" type="button" class="py-2 pr-5 hover:text-text-strong" @click="apply">{{ t['w.basket.summary.voucher_confirm']() }}</button>
    </div>

    <span
      v-if="isSuccess || isError"
      class="text-sm"
      :class="{
        'text-text-strong': isSuccess,
        'text-text-attention': isError,
      }"
    >
      {{ isSuccess ? success : error }}
    </span>
  </div>
</template>

<script setup lang="ts">
import * as t from '~/messages'
import type { IVoucher } from './BasketTypes'
import { useClearPromoCodeMutation, useSetPromoCodeMutation, VoucherTooManyAttemptsError, VoucherVoidError } from '~/composables/queries/voucher'

const promoCode = defineModel<IVoucher | null>('promoCode', { required: true })

const voucher = ref<string>(promoCode.value?.name ?? '')
const focused = ref<boolean>(false)
const success = ref<string | null>(null)
const error = ref<string | null>(null)

const isDirty = computed(() => voucher.value.length > 0)

const isSuccess = computed(() => success.value && !error.value)
const isError = computed(() => error.value && !success.value)

const { mutate } = useSetPromoCodeMutation()

const apply = () => {
  error.value = null
  success.value = null

  mutate(
    { code: voucher.value },
    {
      onSuccess(response) {
        successCallback(response.promoCode)
      },
      onError(error) {
        if (error instanceof VoucherTooManyAttemptsError) {
          errorCallback(t['w.basket.summary.voucher_too_many_attempts']())
        } else if (error instanceof VoucherVoidError) {
          errorCallback(t['w.basket.summary.voucher_error']())
        }
      },
    }
  )
}

const successCallback = (responsePromoCode: IVoucher) => {
  promoCode.value = responsePromoCode
  success.value = t['w.basket.summary.voucher_correct']()
  error.value = null
}

const errorCallback = (errorText: string) => {
  error.value = errorText
  success.value = null
}

const { mutate: mutateClear } = useClearPromoCodeMutation()

const clear = () => {
  mutateClear(undefined, {
    onSuccess() {
      error.value = null
      success.value = null
      voucher.value = ''
      promoCode.value = null
    },
    onError() {
      error.value = t['errors.general.title']()
    },
  })
}

defineExpose({ clear })
</script>
